import React from "react";
import ViewButton from "./Buttons/ViewButton";
import EditButton from "./Buttons/EditButton";
import DeleteButton from "./Buttons/DeleteButton";
import { ButtonProps } from "../Buttons/interface/ButtonInterface";
import LockButton from "./Buttons/LockButton";
import { checkPrivileges } from "../../config/Global";
import { useAppSelector } from "../../utils/hooks/hooks";
import ResultButton from "./Buttons/ResultButton";

interface ActionButtonsProps {
  data: any;
  checkid?: number;
  view?: ButtonProps;
  edit?: ButtonProps;
  deleteButton?: ButtonProps;
  lock?: ButtonProps;
  viewExamDetails?: ButtonProps;
  viewAssessmentResult?: ButtonProps;
  viewInterestAssessment?: ButtonProps;
  viewSkillAssessment?: ButtonProps;
  changePassword?: ButtonProps;
}

/**
 * Renders a set of action buttons based on the provided props.
 *
 * @param {ActionButtonsProps} props - The props for the ActionButtons component.
 * @param {any} props.data - The data object.
 * @param {ButtonProps} props.lock - The props for the lock button.
 * @param {ButtonProps} props.view - The props for the view button.
 * @param {ButtonProps} props.deleteButton - The props for the delete button.
 * @param {ButtonProps} props.edit - The props for the edit button.
 * @param {ButtonProps} props.viewAssessmentResult - The props for the view assessment result button in user management module.
 * @param {ButtonProps} props.viewInterestAssessment - The props for the view interest assessment button in user management module..
 * @param {ButtonProps} props.viewSkillAssessment - The props for the view skill assessment button in user management module..
 * @return {JSX.Element} The rendered set of action buttons.
 */
const ActionButtons: React.FC<ActionButtonsProps> = ({
  data,
  lock,
  checkid,
  view,
  deleteButton,
  edit,
  viewExamDetails,
  viewAssessmentResult,
  viewInterestAssessment,
  viewSkillAssessment,
  changePassword
}) => {
  const userDetail = useAppSelector(state => state.auth.userDetail);

  return (
    <div className="action-column">
      {lock?.action && (
        <LockButton
          data={data?.data}
          permission={
            lock?.permissionKey
              ? checkPrivileges(userDetail, lock?.permissionKey)
              : false
          }
          {...lock}
        />
      )}
       {viewExamDetails?.action && (
        <ResultButton
          data={data?.data}
          permission={
            viewExamDetails?.permissionKey && ![1,3].includes(data?.data?.role_id)
              ? checkPrivileges(
                  userDetail,
                  viewExamDetails?.permissionKey
                )
              : false
          }
          title="View Exam Details"
          iconProps={{ icon: "ExamIcon" }}
          {...viewExamDetails}
        />
      )}
      {viewInterestAssessment?.action && (
        <ResultButton
          data={data?.data}
          permission={
            viewInterestAssessment?.permissionKey && ![1,3].includes(data?.data?.role_id)
              ? checkPrivileges(
                  userDetail,
                  viewInterestAssessment?.permissionKey
                )
              : false
          }
          title="View Interest Discovery Results"
          iconProps={{ icon: "InterestAssessment" }}
          {...viewInterestAssessment}
        />
      )}
      {viewSkillAssessment?.action && (
        <ResultButton
          data={data?.data}
          permission={
            viewSkillAssessment.permissionKey && ![1,3].includes(data?.data?.role_id)
              ? checkPrivileges(userDetail, viewSkillAssessment.permissionKey)
              : false
          }
          title="View Skill Portrait Results"
          iconProps={{ icon: "SkillAssessment" }}
          {...viewSkillAssessment}
        />
      )}
      {viewAssessmentResult?.action && (
        <ResultButton
          data={data?.data}
          permission={
            viewAssessmentResult?.permissionKey && ![1,3].includes(data?.data?.role_id)
              ? checkPrivileges(
                  userDetail,
                  viewAssessmentResult?.permissionKey
                )
              : false
          }
          title="View Career Compass Results"
          iconProps={{ icon: "CareerCompass" }}
          {...viewAssessmentResult}
        />
      )}
      {view?.action && (
        <ViewButton
          data={data?.data}
          permission={
            view.permissionKey
              ? checkPrivileges(userDetail, view.permissionKey)
              : false
          }
          {...view}
        />
      )}
      {edit?.action && (
        <EditButton
          data={data?.data}
          permission={
            edit.permissionKey && checkid !== userDetail?.id
              ? checkPrivileges(userDetail, edit.permissionKey)
              : false
          }
          {...edit}
        />
      )}
      {changePassword?.action && (  
         <ResultButton
         data={data?.data}
         permission={
            edit?.permissionKey && checkid !== userDetail?.id
             ? checkPrivileges(
                 userDetail,
                 edit?.permissionKey
               )
             : false
         }
         title="Change Password"
         iconProps={{ icon: "ChangePassword" }}
         {...changePassword}
       />
      )}
      
      {deleteButton?.action && (
        <DeleteButton
          data={data?.data}
          permission={
            deleteButton.permissionKey && ![1, 3].includes(data?.data?.role_id) && checkid !== userDetail?.id
              ? checkPrivileges(userDetail, deleteButton.permissionKey)
              : false
          }
          {...deleteButton}
        />
      )}
    </div>
  );
};

export default ActionButtons;
